<template>
  <div
    ref="viewRef"
    class="messenger-view-layout"
  >
    <div
      v-bind="getThemeBlockWithBackgroundProps('messenger-header')"
      class="messenger-view-layout__header messenger-header"
    >
      <div class="messenger-header__left">
        <widget-button
          v-if="!sharedSettings.hasOnlyOneActiveChannel"
          v-bind="themeTransparentButtonOnBackgroundProps"
          is-icon-only
          size="28"
          view="transparent"
          @click="$emit('back')"
        >
          <widget-icon
            name="tmi-chevron-left"
            size="20px"
          />
        </widget-button>

        <slot name="channel-icon">
          <div
            v-if="channelType"
            v-bind="getThemeIconProps('messenger-header__channel-icon')"
            class="messenger-header__channel-icon"
            :style="{
              backgroundColor: backgroundContrastColor,
            }"
          >
            <messenger-channel-icon
              :channel-type="channelType"
              size="24px"
            />
          </div>
        </slot>
      </div>

      <div class="messenger-header__center">
        <div class="messenger-header__title">
          <widget-skeleton
            v-if="isLoading"
            class="inline-block vertical-middle"
            type="QChip"
            dark
            width="130px"
            height="15px"
          />
          <template v-else>
            <messenger-text-with-emoji :text="title" />
          </template>
        </div>
        <div class="messenger-header__subtitle">
          <widget-skeleton
            v-if="isLoading"
            class="inline-block vertical-middle"
            type="QChip"
            dark
            width="106px"
            height="8px"
          />
          <template v-else>
            <messenger-text-with-emoji
              :text="subtitle"
              emoji-size="14px"
            />
          </template>
        </div>
      </div>

      <div
        v-if="$slots['header-right']"
        class="messenger-header__right"
      >
        <slot name="header-right" />
      </div>

      <div class="messenger-header__alert">
        <slot name="alert" />
      </div>
    </div>

    <div class="messenger-view-layout__content">
      <slot name="before-content" />

      <div class="messenger-view-layout__scroll-container">
        <widget-scroll
          ref="viewScrollContainerRef"
          :with-shadow="scrollWithShadow"
          :scroll-bar-id="scrollBarId"
          @resize-content-height="scrolledContentHeight = $event"
        >
          <slot />
        </widget-scroll>

        <slot name="after-scroll" />
      </div>

      <div class="messenger-view-layout__footer">
        <slot name="footer-content" />
      </div>
    </div>

    <div class="messenger-view-layout__powered-by">
      <widget-powered-by />
    </div>
  </div>
</template>

<script setup lang="ts">
import WidgetPoweredBy from '@widgets/shared/components/WidgetPoweredBy.vue'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import { useWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import WidgetSkeleton from '@widgets/shared/components/WidgetSkeleton.vue'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import MessengerChannelIcon from '@widgets/messenger/components/MessengerChannelIcon.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import WidgetScroll from '@widgets/shared/components/WidgetScroll.vue'
import { useWidgetViewLayoutSizeObserver } from '@widgets/messenger/compositions/useWidgetViewLayoutSizeObserver'
import type { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'
import { ref, watchEffect } from '@/composition/vue/compositionApi'
import type { AnyTemplateElement } from '@/components/types'

const { scrollWithShadow = true } = defineProps<{
  title: string
  subtitle?: string
  isLoading?: boolean
  channelType?: WebWidgetChannelType
  scrollWithShadow?: boolean
  scrollBarId?: string
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'resize', height: number): void
}>()

const sharedSettings = useWidgetSharedSettings()
const {
  getThemeBlockWithBackgroundProps,
  getThemeIconProps,
  themeTransparentButtonOnBackgroundProps,
  backgroundContrastColor,
} = useWidgetTheme()

const viewRef = ref<AnyTemplateElement | null>(null)
const viewScrollContainerRef = ref<AnyTemplateElement | null>(null)
const scrolledContentHeight = ref(0)

const { height } = useWidgetViewLayoutSizeObserver({
  viewRef,
  viewScrollContainerRef,
  scrolledContentHeight,
})

watchEffect(() => {
  emit('resize', height.value)
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.messenger-view-layout {
  border-radius: 20px;
  overflow: hidden;
  background: $white;
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    position: relative;
    z-index: 1;
  }

  &__content,
  &__scroll-container {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}

.messenger-header {
  @include widget-gradient-bg(messenger-header--gradient);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  color: $white;

  &__left,
  &__right {
    flex-shrink: 0;
    flex-grow: 0;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__center {
    flex-shrink: 1;
    flex-grow: 1;
    min-width: 0;
  }

  &__title {
    @include truncate();
    font-size: 16px;
    font-weight: $bold-font;
    line-height: 24px;
  }

  &__subtitle {
    @include truncate();
    font-size: 14px;
    font-weight: $medium-font;
    line-height: 20px;
    opacity: 0.8;
  }

  &__channel-icon {
    @include widget-color-variants();
    flex-shrink: 0;
    flex-grow: 0;
    background: $white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
  }

  &__alert {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 10;
  }
}
</style>
